import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@app/core/services/toast.service';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import {
    DeliveryNoteBundlesService,
    ToursService,
    PdfService,
} from 'api/services';
import {
    UserRoles,
    LoginOrganizationSite,
    CarrierDeliveryNoteInPool,
    DeliveryNoteInTour,
    GetTourResponse,
} from 'api/models';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { AlertDialogComponent } from '@app/shared/components/alert-dialog/alert-dialog.component';
import { QuestionDialogComponent } from '@app/shared/components/question-dialog/question-dialog.component';
import { QuestionCloseResult } from '@app/shared/models/components/question-close-result';
import { UtilsService } from '@app/core/services/client-services/utils-service/utils.service';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { lastValueFrom } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { triggerDataRefresh } from '@app/stores/searchStore';
import { TourPlanService } from '@app/carrier/services/tourplan.service';
import { getCurrentOrganizationSiteKey } from '@app/stores/appStore';
import {
    SharePermissionsDialogComponent,
    SharePermissionsDialogProps,
} from '@app/carrier/components/share-permissions-dialog/share-permissions-dialog.component';
import { ViewBundleDialogComponent } from '@app/shared/components/view-bundle-dialog/view-bundle-dialog.component';
import { SendWalletInviteComponent } from '@app/shared/components/send-wallet-invite/send-wallet-invite.component';
import { DeliveryNotesService } from 'api/services';
import { setIsTourPlanMode, toggleTourPlanMode } from '@app/stores/appStore';
import { ShowPdfDialogComponent } from '@app/shared/components/show-pdf-dialog/show-pdf-dialog.component';
import {
    TableRowData,
    FolderData,
    TourData,
    DeliveryNoteData,
} from '@app/shared/models/table-data-types';

// Union type for parent context that has accessSiteKey
type ParentWithAccessSiteKey = FolderData | TourData;
import { TourStatus } from 'api-v3/models';

@Injectable({
    providedIn: 'root',
})
export class CarrierViewActionsService {
    constructor(
        private router: Router,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private toastService: ToastService,
        private sessionService: SessionService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private toursService: ToursService,
        private utilsService: UtilsService,
        private errorHandler: ErrorHandler,
        private spinner: NgxSpinnerService,
        private tourPlanService: TourPlanService,
        private deliveryNotesService: DeliveryNotesService,
        private pdfService: PdfService
    ) {}

    /**
     * Shares permissions for selected items
     */
    async sharePermissions(
        selection: FolderData[],
        organizationSite: LoginOrganizationSite
    ): Promise<void> {
        const payload: SharePermissionsDialogProps = {
            organizationSite: organizationSite,
            initialView: 'bundles',
        };

        this.dialog.open(SharePermissionsDialogComponent, {
            data: payload,
            disableClose: true,
        });
    }

    /**
     * Plans a tour with selected items
     */
    async planTour(selection: FolderData[]): Promise<void> {
        // Toggle tour plan mode to show the tour planning component
        toggleTourPlanMode();
    }

    /**
     * Adds selected bundles/delivery notes to a tour
     */
    async addSelectionToTour(
        selection: FolderData[],
        organizationSiteKey?: string
    ): Promise<void> {
        if (!selection || selection.length === 0) {
            this.toastService.warning(
                this.translateService.instant(
                    'SPEDITEUR.TABLE.NO-ITEMS-SELECTED'
                )
            );
            return;
        }

        const orgSiteKey =
            organizationSiteKey ||
            selection[0]?.accessSiteKey ||
            getCurrentOrganizationSiteKey();

        if (!orgSiteKey) {
            this.toastService.error(
                this.translateService.instant('GENERAL-ERROR')
            );
            return;
        }

        const bundleKeys = selection.map(item => item._key);
        const itemsAdded = await this.tourPlanService.addTourPlanBundles(
            bundleKeys,
            orgSiteKey
        );

        if (itemsAdded) {
            this.toastService.success(
                this.translateService.instant(
                    'SPEDITEUR.TABLE.ITEMS-ADDED-TO-TOUR'
                )
            );
        } else {
            this.toastService.info(
                this.translateService.instant(
                    'SPEDITEUR.TABLE.ITEMS-ALREADY-IN-TOUR'
                )
            );
        }
    }

    /**
     * Removes selected items from a tour
     */
    async removeSelectionFromTour(
        selection: DeliveryNoteData[]
    ): Promise<void> {
        if (!selection || selection.length === 0) {
            this.toastService.warning(
                this.translateService.instant(
                    'SPEDITEUR.TABLE.NO-ITEMS-SELECTED'
                )
            );
            return;
        }

        const carrierDeliveryNotes: CarrierDeliveryNoteInPool[] = selection.map(
            note => ({
                ...note,
                carrierNumberPlateIncoming: '',
                carrierNumberPlateOutgoing: '',
                checkinKey: '',
                externalTourName: '',
                loadCarriers: [],
                tourKey: '',
                tourNumber: '',
                partOfTour: false,
                deliveryNoteBundleStatus: 'sent',
            })
        );
        this.tourPlanService.removeTourPlanDeliveryNotes(carrierDeliveryNotes);
        this.toastService.success(
            this.translateService.instant(
                'SPEDITEUR.TABLE.ITEMS-REMOVED-FROM-TOUR'
            )
        );
    }

    /**
     * Checks out a tour
     */
    async checkoutTour(selection: TourData[]): Promise<void> {
        if (!selection || selection.length === 0) {
            this.toastService.warning(
                this.translateService.instant(
                    'SPEDITEUR.TABLE.NO-ITEMS-SELECTED'
                )
            );
            return;
        }

        // Validate that all selected tours are ready for checkout
        const invalidTours = selection.filter(
            tour =>
                tour.status === TourStatus.Closed ||
                !tour.deliveryNoteKeys ||
                tour.deliveryNoteKeys.length === 0
        );

        if (invalidTours.length > 0) {
            return;
        }

        // Navigate to tour checkout page
        this.router.navigate([RoutingEndpoints.carrier, 'checkout-tour'], {
            state: { selectedTours: selection },
        });
    }

    /**
     * Checks out tour selection with wallet invite dialog
     */
    async checkoutTourSelection(selection: TourData[]): Promise<boolean> {
        if (!selection || selection.length === 0) {
            this.toastService.warning(
                this.translateService.instant(
                    'SPEDITEUR.TABLE.NO-ITEMS-SELECTED'
                )
            );
            return false;
        }

        // Import the tour wallet invite component and open dialog
        const { SendTourWalletInviteComponent } = await import(
            '@app/shared/components/send-wallet-invite/send-tour-wallet-invite/send-tour-wallet-invite.component'
        );

        const organizationSiteKey =
            selection[0].accessSiteKey || getCurrentOrganizationSiteKey();
        const organizationName =
            this.sessionService.getSession().organizations[0]?.name;

        const dialog = this.dialog.open(SendTourWalletInviteComponent, {
            minWidth: '360px',
            data: {
                tour: selection[0],
                organizationSiteKey: organizationSiteKey,
                organizationName: organizationName,
                isCheckout: true,
            },
            disableClose: true,
        });

        return new Promise(resolve => {
            dialog.afterClosed().subscribe(async () => {
                // Clear the tour from selection after checkout
                const { clearSelection } = await import(
                    '@app/stores/selectionStore'
                );
                clearSelection();

                // Trigger data refresh to reload the table
                triggerDataRefresh();
                resolve(true);
            });
        });
    }

    /**
     * Views tour details
     */
    viewTourDetails(tour: TourData): void {
        this.router.navigate([
            RoutingEndpoints.carrier,
            'tour-details',
            tour._key,
        ]);
    }

    /**
     * Views bundle details
     */
    viewBundleDetails(bundle: FolderData): void {
        this.dialog.open(ViewBundleDialogComponent, {
            data: {
                deliveryNoteBundle: bundle,
                organizationSiteKey: bundle.accessSiteKey,
                context: 'carrier',
            },
            disableClose: true,
            width: '800px',
        });
    }

    /**
     * Downloads delivery bundle as PDF
     */
    async downloadDeliveryBundle(bundle: FolderData): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                bundle.accessSiteKey || getCurrentOrganizationSiteKey();
            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const response = await lastValueFrom(
                this.deliveryNotesService.getCarrierDeliveryNotePdfsForBundle({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteBundleKey: bundle._key,
                })
            );

            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const bundleNumber = bundle.bundleNumber;
            a.download = bundleNumber.replace('.', '-') + '.pdf';
            a.click();
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Sends wallet invite for a bundle
     */
    sendWalletInvite(bundle: FolderData): void {
        this.dialog.open(SendWalletInviteComponent, {
            minWidth: '360px',
            maxWidth: '90vw',
            data: {
                dlsDeliveryNoteBundle: bundle,
                organizationSiteKey:
                    bundle.accessSiteKey || getCurrentOrganizationSiteKey(),
                isCarrier: true,
            },
            disableClose: true,
        });
    }

    /**
     * Deletes a tour
     */
    async deleteTour(tour: TourData): Promise<void> {
        this.utilsService.setCloseButtonVisibilty(false);
        const dialogRef = this.dialog.open(QuestionDialogComponent, {
            minWidth: '360px',
            data: {
                msg: 'CONFIRM-DELETE-DIALOG.SUBHEADLINE',
                iconClass: '',
                icon: 'delete',
                headline: 'CONFIRM-DELETE-DIALOG.HEADLINE',
                confirmText: 'CONFIRM-DELETE-DIALOG.BUTTON-DELETE',
                cancelText: 'CONFIRM-DELETE-DIALOG.BUTTON-CANCEL',
                refNumber: tour.tourNumber,
            },
            disableClose: true,
        });

        dialogRef
            .afterClosed()
            .subscribe(async (result: QuestionCloseResult) => {
                this.utilsService.setCloseButtonVisibilty(true);
                if (result === QuestionCloseResult.YES) {
                    try {
                        await lastValueFrom(
                            this.toursService.deleteTour({
                                organizationSiteKey: tour.accessSiteKey,
                                tourKey: tour._key,
                            })
                        );

                        this.toastService.success(
                            this.translateService.instant(
                                'SPEDITEUR.TOASTS.TOUR-DELETED'
                            )
                        );

                        // Trigger data refresh to reload the table
                        triggerDataRefresh();
                    } catch (error) {
                        this.toastService.error(
                            this.translateService.instant('GENERAL-ERROR')
                        );
                        this.errorHandler.handleError(error);
                    }
                }
            });
    }

    /**
     * Exports selected items
     */
    async exportSelection(
        selection: TableRowData[],
        format: string = 'pdf'
    ): Promise<void> {
        if (!selection || selection.length === 0) {
            this.toastService.warning(
                this.translateService.instant(
                    'SPEDITEUR.NO-ITEMS-SELECTED-FOR-EXPORT'
                )
            );
            return;
        }

        this.toastService.info('Export feature not yet implemented');
    }

    /**
     * Prints delivery notes for selected items
     */
    async printDeliveryNotes(selection: TableRowData[]): Promise<void> {
        if (!selection || selection.length === 0) {
            this.toastService.warning(
                this.translateService.instant(
                    'SPEDITEUR.NO-ITEMS-SELECTED-FOR-PRINT'
                )
            );
            return;
        }

        this.toastService.info('Print feature not yet implemented');
    }

    /**
     * Scans QR code for tour operations
     */
    async scanQRCode(): Promise<void> {
        this.toastService.info('QR code scanning feature not yet implemented');
    }

    /**
     * Checks if the user can perform tour operations
     */
    canPerformTourOperations(): boolean {
        // check permissions
        return (
            this.sessionService.isOrganizationAdmin ||
            this.sessionService.isSiteAdmin ||
            this.sessionService.isServiceProvider
        );
    }

    /**
     * Validates tour selection for checkout
     */
    isValidTourCheckoutSelection(selection: TourData[]): boolean {
        if (!selection || selection.length === 0) return false;

        return selection.every(
            tour =>
                tour.status !== TourStatus.Closed &&
                tour.deliveryNoteKeys &&
                tour.deliveryNoteKeys.length > 0
        );
    }

    /**
     * Exits tour plan mode
     */
    exitTourPlanMode(): void {
        setIsTourPlanMode(false);
        this.toastService.info(
            this.translateService.instant(
                'SPEDITEUR.TOUR-PLAN-MODE-DEACTIVATED'
            )
        );
    }

    /**
     * Downloads a single delivery note PDF
     * @param deliveryNote
     * @param parentBundle (optional) - parent bundle if in expanded view
     */
    async downloadDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: ParentWithAccessSiteKey
    ): Promise<void> {
        this.spinner.show();
        try {
            // try parent bundle key first, then note's key, then current org
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                getCurrentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const response = await lastValueFrom(
                this.pdfService.getCarrierDeliveryNoteCombinedPdf({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteKey: deliveryNote._key,
                })
            );

            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const fileName = deliveryNote.number || deliveryNote._key;
            a.download = fileName.replace('.', '-') + '.pdf';
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Views a single delivery note in a dialog
     * @param deliveryNote
     * @param parentBundle (optional) - parent bundle if in expanded view
     */
    async viewDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: ParentWithAccessSiteKey
    ): Promise<void> {
        this.spinner.show();
        try {
            // try parent bundle key first, then note's key, then current org
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                getCurrentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const blobData = await lastValueFrom(
                this.pdfService.getCarrierDeliveryNoteCombinedPdf({
                    organizationSiteKey: organizationSiteKey,
                    deliveryNoteKey: deliveryNote._key,
                })
            );

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Edits a tour (enters tour plan mode)
     */
    async editTour(tour: TourData): Promise<void> {
        await this.tourPlanService.setTourPlanMode(
            true,
            tour.accessSiteKey || getCurrentOrganizationSiteKey() || undefined,
            tour
        );

        this.toastService.info(
            this.translateService.instant('SPEDITEUR.TOUR-EDIT-MODE-ACTIVATED')
        );
    }

    /**
     * Downloads tour as PDF
     */
    async downloadTour(tour: TourData): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                tour.accessSiteKey || getCurrentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const response = await lastValueFrom(
                this.toursService.getToursPdfs({
                    organizationSiteKey: organizationSiteKey,
                    tourKey: tour._key,
                })
            );

            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const tourNumber = tour.tourNumber || tour._key;
            a.download = tourNumber.replace('.', '-') + '.pdf';
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Views tour as PDF
     */
    async viewTourPdf(tour: TourData): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                tour.accessSiteKey || getCurrentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            const blobData = await lastValueFrom(
                this.toursService.getToursPdfs({
                    organizationSiteKey: organizationSiteKey,
                    tourKey: tour._key,
                })
            );

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Sends wallet invite for a tour
     */
    sendTourWalletInvite(tour: TourData): void {
        // Import the tour wallet invite component
        import(
            '@app/shared/components/send-wallet-invite/send-tour-wallet-invite/send-tour-wallet-invite.component'
        ).then(({ SendTourWalletInviteComponent }) => {
            this.dialog.open(SendTourWalletInviteComponent, {
                minWidth: '360px',
                data: {
                    tour: tour,
                    organizationSiteKey:
                        tour.accessSiteKey || getCurrentOrganizationSiteKey(),
                    organizationName:
                        this.sessionService.getSession().organizations[0]?.name,
                    isCheckout: false,
                },
                disableClose: true,
            });
        });
    }

    /**
     * Removes a delivery note from a tour
     */
    async removeDeliveryNoteFromTour(
        deliveryNote: DeliveryNoteData,
        parentTour: TourData
    ): Promise<void> {
        this.utilsService.setCloseButtonVisibilty(false);
        const dialogRef = this.dialog.open(QuestionDialogComponent, {
            minWidth: '360px',
            data: {
                msg: 'CONFIRM-DELETE-DIALOG.SUBHEADLINE',
                iconClass: '',
                icon: 'cut',
                headline: 'SPEDITEUR.CONFIRM-REMOVE-DELIVERY-NOTE.HEADLINE',
                confirmText:
                    'SPEDITEUR.CONFIRM-REMOVE-DELIVERY-NOTE.BUTTON-REMOVE',
                cancelText: 'CONFIRM-DELETE-DIALOG.BUTTON-CANCEL',
                refNumber: deliveryNote.number,
            },
            disableClose: true,
        });

        dialogRef
            .afterClosed()
            .subscribe(async (result: QuestionCloseResult) => {
                this.utilsService.setCloseButtonVisibilty(true);
                if (result === QuestionCloseResult.YES) {
                    try {
                        // First get the current tour to access its deliveryNoteKeys
                        const orgSiteKey =
                            parentTour.accessSiteKey ||
                            getCurrentOrganizationSiteKey();

                        if (!orgSiteKey) {
                            this.toastService.error(
                                this.translateService.instant(
                                    'ERROR.ORGANIZATION_SITE_KEY_MISSING'
                                )
                            );
                            return;
                        }

                        const currentTour = await lastValueFrom(
                            this.toursService.getTour({
                                organizationSiteKey: orgSiteKey,
                                tourKey: parentTour._key,
                            })
                        );

                        // Remove the delivery note key from the array
                        const updatedDeliveryNoteKeys =
                            currentTour.deliveryNotes
                                ?.map((note: DeliveryNoteInTour) => note._key)
                                .filter(
                                    (key: string) => key !== deliveryNote._key
                                ) || [];

                        // Update the tour with the modified deliveryNoteKeys
                        await lastValueFrom(
                            this.toursService.updateTour({
                                organizationSiteKey: orgSiteKey,
                                tourKey: parentTour._key,
                                body: {
                                    deliveryNoteKeys: updatedDeliveryNoteKeys,
                                    externalTourName:
                                        currentTour.externalTourName,
                                },
                            })
                        );

                        this.toastService.success(
                            this.translateService.instant(
                                'SPEDITEUR.DELIVERY-NOTE-REMOVED-FROM-TOUR-SUCCESS'
                            )
                        );

                        // Trigger data refresh to reload the table
                        triggerDataRefresh();
                    } catch (error) {
                        this.toastService.error(
                            this.translateService.instant('GENERAL-ERROR')
                        );
                        this.errorHandler.handleError(error);
                    }
                }
            });
    }
}
